// Mirrors backend/src/Core/VetCore.BackEnd.Application/DTOs/ClinicalDtos.cs's
// `PatientClinicalTimelineEntryDto` 1:1 (API keeps PascalCase, see `PropertyNamingPolicy = null` in
// Program.cs). Every DTO below is a literal field-for-field copy of its
// `features/patients/data/dtos/clinical-history.dto.ts` equivalent -- duplicated here rather than
// imported cross-feature: the client and patient endpoints have different projections.
// `GET /api/clients/{clientId}/clinical-timeline` returns a bare array of entries,
// already sorted newest-first and deduplicated by the backend: no `PagedDto` envelope, no per-patient
// bundle wrapper.

// Matches the C# enum's default int serialization: Consultation = 0, Diagnosis = 1,
// Vaccination = 2, Treatment = 3, Deworming = 4.
export enum ClientClinicalTimelineEntryTypeDto {
  Consultation = 0,
  Diagnosis = 1,
  Vaccination = 2,
  Treatment = 3,
  Deworming = 4,
}

// One flat entry of the client-scoped merged timeline. Beyond the common fields, each block below is
// populated only for the entry kinds its backend doc-comment names and stays null for every other,
// so a `Type` check is what narrows a payload -- there is no per-kind DTO to discriminate on.
export interface ClientClinicalTimelineEntryDto {
  readonly Id: string;
  readonly Type: ClientClinicalTimelineEntryTypeDto;
  readonly Title: string;
  readonly Date: string | null;
  readonly Description: string | null;
  readonly SourceId: string;
  readonly PatientId: string | null;
  readonly PatientName: string | null;
  // Consultation and Treatment entries.
  readonly VeterinarianName: string | null;
  // Consultation entries.
  readonly DepartmentName: string | null;
  readonly StatusCode: number | null;
  readonly StatusLabel: string | null;
  readonly InvoiceId: string | null;
  readonly InvoiceTotal: number | null;
  readonly InvoicePaymentStatus: string | null;
  readonly InvoiceIsPending: boolean | null;
  readonly NoteId: string | null;
  readonly NoteTitle: string | null;
  // Diagnosis and Treatment entries: the consultation the record hangs off.
  readonly ConsultationId: string | null;
  // Diagnosis entries.
  readonly DiagnosisCode: string | null;
  readonly DiagnosisCategory: string | null;
  readonly DiagnosisType: string | null;
  // Vaccination entries. `VaccinationGivenDate` is NOT redundant with `Date`, which falls back to the
  // record's creation timestamp -- see the backend DTO's doc-comment.
  readonly VaccinationGivenDate: string | null;
  // Serialized `yyyy-MM-dd` (C# `DateOnly`).
  readonly VaccinationExpiryDate: string | null;
  // Empty in every sandbox payload observed so far, so a tenant that never fills it may well omit
  // the key entirely -- optional, not merely nullable.
  readonly VaccinationDiseases?: readonly string[] | null;
  // Treatment entries. `TreatmentTypeCode`/`TreatmentTypeLabel` are unconfirmed against a live
  // tenant, hence optional for the same reason as `VaccinationDiseases` above.
  readonly TreatmentQuantity: number | null;
  readonly TreatmentUnit: string | null;
  readonly TreatmentAdministrationMethod: string | null;
  readonly TreatmentTypeCode?: number | null;
  readonly TreatmentTypeLabel?: string | null;
  // Every note-attachment stub of this consultation (level 1 of the attachments feature), populated
  // whenever `Type === Consultation` -- literal copy of patients' `ClinicalTimelineEntryDto
  // .AttachmentNotes`, see that file's own doc-comment.
  readonly AttachmentNotes: readonly ClientConsultationAttachmentNoteDto[];
}

// Literal copy of patients' `ConsultationAttachmentNoteDto` (see that file's own doc-comment).
export interface ClientConsultationAttachmentNoteDto {
  readonly NoteId: string;
  readonly Title: string | null;
  readonly TypeLabel: string | null;
  readonly Date: string | null;
}

export interface ClientDiagnosisDto {
  readonly ExternalId: string;
  readonly ConsultationId: string | null;
  readonly PatientId: string | null;
  readonly Code: string | null;
  readonly Name: string | null;
  readonly Description: string | null;
  readonly Category: string | null;
  readonly Type: string | null;
  readonly Date: string | null;
  readonly Created: string | null;
  readonly Modified: string | null;
  readonly SupervisingVeterinarianId: string | null;
  readonly SupervisingVeterinarianName: string | null;
}

export interface ClientConsultationMedicineDto {
  readonly ExternalId: string;
  readonly ConsultationId: string | null;
  readonly PatientId: string | null;
  readonly Vaccination: boolean;
  readonly Name: string | null;
  readonly Used: string | null;
  readonly Quantity: number | null;
  readonly Unit: string | null;
  readonly AdministrationMethodCode: number | null;
  readonly AdministrationMethodLabel: string | null;
  readonly VaccinationTypeLabel: string | null;
  readonly ActiveSubstance: string | null;
  readonly ManufacturerData: string | null;
  readonly Preparation: string | null;
  readonly Contraindications: string | null;
  readonly Instructions: string | null;
  readonly WithdrawalPeriods: string | null;
  readonly SupervisingVeterinarianId: string | null;
  readonly SupervisingVeterinarianName: string | null;
  readonly MedicationDiagnosisId: string | null;
  readonly InvoiceRowId: string | null;
  readonly PerformedById: string | null;
  readonly PerformedByName: string | null;
  readonly UsageTypeCode: number | null;
  readonly UsageTypeLabel: string | null;
  readonly AdministrationHours: number | null;
  readonly AdministrationSpeed: number | null;
  readonly MedicationDurationDays: number | null;
  readonly ControlledSubstance: boolean | null;
  readonly VaccinationTypeCode: number | null;
}

export interface ClientVitalsSnapshotDto {
  readonly Text: string | null;
  readonly Weight: number | null;
  readonly WeightTimestamp: string | null;
  readonly WeightUnits: string | null;
  readonly Temperature: number | null;
  readonly TemperatureTimestamp: string | null;
  readonly TemperatureUnits: string | null;
  readonly HeartRate: number | null;
  readonly HeartRateTimestamp: string | null;
  readonly RespiratoryRate: number | null;
  readonly RespiratoryRateTimestamp: string | null;
  readonly BodyConditionScore: string | null;
  readonly BodyConditionScoreTimestamp: string | null;
  readonly BloodPressure: string | null;
  readonly BloodPressureTimestamp: string | null;
  readonly BloodPressureBodyPositionLabel: string | null;
  readonly PeripheralOxygenSaturation: number | null;
  readonly PeripheralOxygenSaturationTimestamp: string | null;
  readonly BloodGlucose: number | null;
  readonly CentralVenousPressure: number | null;
  readonly BloodPressureBodyPositionCode: number | null;
}

// On-demand payload for the client-scoped "Detalle de Consulta" expansion -- backed by
// `GET /api/clients/{clientId}/patients/{patientId}/consultations/{consultationId}` (see
// `ClientsController.GetPatientConsultationDetail`), which delegates to the exact same
// `PatientClinicalHistoryQueryService.GetPatientConsultationDetailAsync` call as the patients-scoped
// route -- so this DTO is a literal copy of patients' `PatientConsultationDetailDto`.
export interface ClientConsultationDetailDto {
  readonly ExternalId: string;
  readonly Complaint: string | null;
  readonly AdmittedTime: string | null;
  readonly Started: string | null;
  readonly Finished: string | null;
  readonly Ward: string | null;
  readonly PatientLocation: string | null;
  readonly Triage: string | null;
  readonly HoldingPlaceNumber: string | null;
  readonly ReferringOrganization: string | null;
  readonly ReferringVeterinarian: string | null;
  readonly IntegrationStatus: string | null;
  readonly ExternalSent: boolean | null;
  readonly ReportToAuthorities: boolean | null;
  readonly Vitals: ClientVitalsSnapshotDto | null;
  readonly DischargeInstructionsText: string | null;
  readonly DischargeInstructionsPrintedAt: string | null;
  readonly NotesRaw: readonly Record<string, unknown>[];
  readonly Diagnoses: readonly ClientDiagnosisDto[];
  readonly Items: readonly ClientConsultationMedicineDto[];
  // Opcional, no solo nullable: el backend lo declara con valor por defecto
  // (`IReadOnlyCollection<PrescriptionDto>? Prescriptions = null`), así que la clave puede faltar
  // por completo del JSON. El mapper ya normaliza con `?? []`.
  readonly Prescriptions?: readonly ClientPrescriptionDto[] | null;
  // Typed projection of the very same `consultationnote` rows `NotesRaw` carries. Opcional por la
  // misma razón que `Prescriptions`, y aquí la distinción importa: si la clave falta, el backend es
  // anterior a este campo (el mapper parsea `NotesRaw`); un array vacío es un "esta consulta no
  // tiene ninguna nota con texto" autoritativo.
  readonly Notes?: readonly ClientConsultationNoteDto[] | null;
  // Level 2 of the attachments feature -- literal copy of patients' `PatientConsultationDetailDto
  // .Attachments`, see that file's own doc-comment.
  readonly Attachments: readonly ClientConsultationAttachmentDto[];
}

// Literal copy of patients' `ConsultationAttachmentDto` (see that file's own doc-comment).
export interface ClientConsultationAttachmentDto {
  readonly NoteId: string;
  readonly UploadId: string;
  readonly FileName: string;
  readonly SizeBytes: number;
  readonly ContentType: string;
}

// Mirrors the backend's `ConsultationNoteDto` (see ClinicalDtos.cs). `AuthorName` llega ya resuelto
// a nombre visible, nunca como id de usuario de ProVet; `TypeLabel` es null cuando ProVet manda un
// código numérico o una URI en vez de texto legible -- resultado esperado, no un fallo de mapeo.
export interface ClientConsultationNoteDto {
  readonly Text: string;
  readonly DateAdded: string | null;
  readonly TypeLabel: string | null;
  readonly AuthorName: string | null;
}

// Mirrors the backend's `PrescriptionItemDto` (see ClinicalDtos.cs).
export interface ClientPrescriptionItemDto {
  readonly ExternalId: string;
  readonly Code: string | null;
  readonly Name: string | null;
  readonly Quantity: number | null;
  readonly Instructions: string | null;
  readonly Units: number | null;
  readonly Unit: string | null;
  readonly PackageDescription: string | null;
  readonly UsageSize: number | null;
  readonly DosageUnits: number | null;
  readonly WithdrawalPeriods: string | null;
  readonly Refills: number | null;
  readonly DosageForm: string | null;
}

// Mirrors the backend's `PrescriptionDto` (see ClinicalDtos.cs) 1:1.
export interface ClientPrescriptionDto {
  readonly ExternalId: string;
  readonly ConsultationId: string | null;
  readonly ClientId: string | null;
  readonly PatientId: string | null;
  readonly PrescriberId: string | null;
  readonly DepartmentId: string | null;
  readonly IssueTime: string | null;
  readonly ValidMonths: number | null;
  readonly ValidDays: number | null;
  readonly Controlled: boolean | null;
  readonly Microbial: boolean | null;
  readonly Diagnosis: string | null;
  readonly PrintedTime: string | null;
  readonly Archived: boolean | null;
  readonly Created: string | null;
  readonly Items: readonly ClientPrescriptionItemDto[];
  readonly PrescriberName: string | null;
  readonly DocumentId: string | null;
  readonly SentAtUtc: string | null;
}

// Per-patient bundle backing `GET /api/clients/{clientId}/prescriptions` -- mirrors the backend's
// `ClientPatientPrescriptionsDto` (see ClinicalDtos.cs).
export interface ClientPatientPrescriptionsDto {
  readonly PatientId: string;
  readonly PatientName: string;
  readonly Prescriptions: readonly ClientPrescriptionDto[];
}

// Root response for `GET /api/clients/{clientId}/prescriptions` -- mirrors the backend's
// `ClientPrescriptionsAggregateDto` (see ClinicalDtos.cs). Empty `Patients` for clients with no
// patients, not an error.
export interface ClientPrescriptionsAggregateDto {
  readonly Patients: readonly ClientPatientPrescriptionsDto[];
}

// Row of `GET /api/clients/{clientId}/consultations/summary` -- literal copy of patients'
// `ConsultationSummaryDto` (see that file's own doc-comment). The listing merges every pet of the
// tutor, so `PatientId` is what the table's "mascota" column resolves against `client().patients`.
export interface ClientConsultationSummaryDto {
  readonly ExternalId: string;
  readonly Title: string | null;
  readonly Date: string | null;
  readonly TypeLabel: string | null;
  readonly PatientId: string | null;
}

// `PagedDto<ConsultationSummaryDto>` envelope (see PagedDto.cs) -- same shape as `PagedInvoicesDto`
// in this feature's `invoice.dto.ts`.
export interface PagedClientConsultationSummariesDto {
  readonly TotalCount: number;
  readonly TotalPages: number;
  readonly Items: readonly ClientConsultationSummaryDto[];
}

// `GET /api/clients/{clientId}/patients/{patientId}/consultations/{consultationId}/overview` --
// literal copy of patients' `ConsultationOverviewDto`, served by the SAME backend method
// (`PatientClinicalHistoryQueryService.GetConsultationOverviewSectionAsync`, routed through
// `ConsultationSectionCatalog.Resolve`, shared verbatim by `PatientsController` and
// `ClientsController`) through the `ClientsRead`-gated mirror route (same relationship as
// `ClientConsultationDetailDto` above) -- so the wire payload has always carried
// `NotesCount`/`DiagnosesCount`/`DischargeCount`/`HasVitalsSnapshot` alongside
// `HasConsultationItems` for a client-origin visit too. Only this frontend-side DTO declared
// `HasConsultationItems` and left the other four off, so a client-origin consultation silently
// rendered its Notas/Diagnósticos/Alta/Constantes badges/dot absent even though the data was on the
// wire the whole time.
export interface ClientConsultationOverviewDto {
  readonly ExternalId: string;
  readonly Complaint: string | null;
  readonly Status: number | null;
  readonly StatusLabel: string | null;
  readonly TypeLabel: string | null;
  readonly AdmittedTime: string | null;
  readonly Started: string | null;
  readonly Finished: string | null;
  readonly Ward: string | null;
  readonly PatientLocation: string | null;
  readonly Triage: string | null;
  readonly VeterinarianName: string | null;
  readonly DepartmentName: string | null;
  readonly InvoiceId: string | null;
  readonly InvoiceTotal: number | null;
  readonly InvoiceIsPending: boolean | null;
  readonly NotesCount: number | null;
  readonly DiagnosesCount: number;
  readonly DischargeCount: number | null;
  readonly HasVitalsSnapshot: boolean;
  readonly HasConsultationItems: boolean;
}

// Literal copy of patients' `ConsultationDischargeDto` (see that file's own doc-comment).
export interface ClientConsultationDischargeDto {
  readonly Text: string | null;
  readonly PrintedAt: string | null;
}

// Literal copy of patients' `PatientTreatmentDto` -- the billable `consultationitem` lines behind
// the `procedures` section. New here: the client-scoped timeline never fetched this
// per-consultation fan-out (a route it deliberately avoids), but the `procedures` section of the
// consultation detail page does.
export interface ClientTreatmentDto {
  readonly ExternalId: string;
  readonly ConsultationId: string | null;
  readonly Name: string | null;
  readonly Instructions: string | null;
  readonly Used: string | null;
  readonly Quantity: number | null;
  readonly Unit: string | null;
  readonly SupervisingVeterinarianName: string | null;
  readonly AdministrationMethod: string | null;
  readonly TypeCode: number | null;
  readonly TypeLabel: string | null;
}

// Literal copy of patients' `ConsultationSupplyDto`, backing the `supplies` section. New here for
// the same reason as `ClientTreatmentDto` above.
export interface ClientConsultationSupplyDto {
  readonly ExternalId: string;
  readonly ConsultationId: string | null;
  readonly PatientId: string | null;
  readonly Name: string | null;
  readonly Instructions: string | null;
  readonly Used: string | null;
  readonly Quantity: number | null;
  readonly Unit: string | null;
  readonly PackageDescription: string | null;
  readonly UsageSize: number | null;
  readonly SupervisingVeterinarianId: string | null;
  readonly SupervisingVeterinarianName: string | null;
  readonly PerformedById: string | null;
  readonly PerformedByName: string | null;
}

// Literal copy of patients' `ConsultationLaboratoryAnalysisDto`, backing the `analysis` section.
// New here for the same reason as `ClientTreatmentDto` above.
export interface ClientConsultationLaboratoryAnalysisDto {
  readonly ExternalId: string;
  readonly ConsultationId: string | null;
  readonly PatientId: string | null;
  readonly Name: string | null;
  readonly Code: string | null;
  readonly Used: string | null;
  readonly Result: string | null;
  readonly ResultNumeric: number | null;
  readonly ResultEntered: string | null;
  readonly ReferenceLow: string | null;
  readonly ReferenceHigh: string | null;
  readonly MeasurementUnit: string | null;
  readonly Remarks: string | null;
  readonly Approved: boolean | null;
  readonly ApprovedDate: string | null;
  readonly SupervisingVeterinarianId: string | null;
  readonly SupervisingVeterinarianName: string | null;
  readonly PerformedById: string | null;
  readonly PerformedByName: string | null;
  readonly ReferralId: string | null;
  readonly Device: string | null;
}

// Literal copy of patients' `PatientTreatmentPlanDto`, backing the `plans` section. New here for
// the same reason as `ClientTreatmentDto` above -- see `ClientTreatmentPlan`'s own doc-comment
// in `clinical-history.model.ts`, which existed as a type long before anything could populate it.
export interface ClientTreatmentPlanDto {
  readonly ExternalId: string;
  readonly ConsultationId: string;
  readonly StartDate: string | null;
  readonly EndDate: string | null;
  readonly Ongoing: boolean | null;
  readonly Timings: string | null;
  readonly EveryNDays: number | null;
  readonly RepetitionsPerDay: number | null;
  readonly IsCabinetItem: boolean | null;
  readonly DoseCount: number | null;
  readonly ItemKind: string | null;
  readonly ItemName: string | null;
  readonly ItemCode: string | null;
  readonly Quantity: number | null;
  readonly Unit: string | null;
  readonly PackageDescription: string | null;
  readonly UsageSize: number | null;
  readonly Instructions: string | null;
}

// Literal copy of patients' `ConsultationSectionDtoMap` (see that file's own doc-comment for why
// `vitals` is nullable) -- the client-scoped routes serve the exact same section payloads.
export interface ClientConsultationSectionDtoMap {
  readonly overview: ClientConsultationOverviewDto;
  readonly vitals: ClientVitalsSnapshotDto | null;
  readonly diagnoses: readonly ClientDiagnosisDto[];
  readonly medicines: readonly ClientConsultationMedicineDto[];
  readonly procedures: readonly ClientTreatmentDto[];
  readonly supplies: readonly ClientConsultationSupplyDto[];
  readonly analysis: readonly ClientConsultationLaboratoryAnalysisDto[];
  readonly plans: readonly ClientTreatmentPlanDto[];
  readonly prescriptions: readonly ClientPrescriptionDto[];
  readonly notes: readonly ClientConsultationNoteDto[];
  readonly discharge: ClientConsultationDischargeDto;
  readonly attachments: readonly ClientConsultationAttachmentDto[];
}
